// ============================================
// FILE: frontend/src/pages/admin/AdminMatchesPage.jsx
// PURPOSE: Admin page to view and manage 90%+ matches
// ============================================

import { useState, useEffect } from 'react';
import { useNotification } from '../../hooks/useNotification';
import { useMatching } from '../../hooks/useMatching';
import { connectionsAPI } from '../../api/connections.api';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription
} from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { MatchCard } from '../../components/matching/MatchCard';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Loader2, AlertCircle, Zap } from 'lucide-react';
import { MatchDetailsModal } from '../../components/matching/MatchDetailsModal';
import { matchingAPI } from '../../api/matching.api';

// ========================================
// MAIN PAGE COMPONENT
// ========================================

export default function AdminMatchesPage() {
  console.log('🎨 [AdminMatchesPage] Page mounted');

  // ========================================
  // HOOKS
  // ========================================

  const { showSuccess, showError } = useNotification();
  const {
    matches,
    loading,
    reviewingId,
    runningMatching,
    error,
    pagination,
    runMatching,
    fetchMatches,
    reviewMatch
  } = useMatching();

  // ========================================
  // STATE
  // ========================================

  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [sortBy, setSortBy] = useState('score');
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [viewingMatch, setViewingMatch] = useState(null);
  const [connectingId, setConnectingId] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // ========================================
  // EFFECTS
  // ========================================

  useEffect(() => {
    console.log('[AdminMatchesPage] Fetching matches...', {
      page: currentPage,
      status: statusFilter,
      sortBy
    });
    fetchMatches(currentPage, 20, statusFilter, sortBy);
  }, [currentPage, statusFilter, sortBy, fetchMatches]);

  // ========================================
  // EVENT HANDLERS
  // ========================================

    const handleViewDetails = async (match) => {
    console.log('[AdminMatchesPage] 🔍 Fetching full details for match:', match.id);
    
    setLoadingDetails(true);
    try {
      const response = await matchingAPI.getMatchDetails(match.id);
      console.log('[AdminMatchesPage] ✅ Full match details loaded:', response.match);
      setViewingMatch(response.match);
    } catch (err) {
      console.error('[AdminMatchesPage] ❌ Failed to load match details:', err);
      showError('Error', 'Failed to load match details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleRunMatching = async () => {
    console.log('[AdminMatchesPage] 🔧 Run matching clicked');

    try {
      const result = await runMatching();
      console.log('[AdminMatchesPage] ✅ Matching complete:', result);

      showSuccess(
        'Matching Complete',
        `${result.stats?.matchesCreated} new matches found out of ${result.stats?.totalAttempts} comparisons`
      );

      // Refresh matches list
      console.log('[AdminMatchesPage] Refreshing matches after matching run');
      fetchMatches(1, 20, statusFilter, sortBy);
      setCurrentPage(1);
    } catch (err) {
      console.error('[AdminMatchesPage] ❌ Run matching error:', err);
      showError('Matching Failed', error || 'Failed to run matching algorithm');
    }
  };

  const handleApproveMatch = async (matchId) => {
    console.log('[AdminMatchesPage] ✅ Approving match:', matchId);

    try {
      await reviewMatch(matchId, 'approve');
      console.log('[AdminMatchesPage] Match approved successfully');

      showSuccess('Match Approved', 'Match marked as approved');
      setSelectedMatchId(null);
      
      // Refresh list
      console.log('[AdminMatchesPage] Refreshing matches after approval');
      fetchMatches(currentPage, 20, statusFilter, sortBy);
    } catch (err) {
      console.error('[AdminMatchesPage] ❌ Approve error:', err);
      showError('Failed', error || 'Failed to approve match');
    }
  };

  const handleRejectMatch = async (matchId) => {
    console.log('[AdminMatchesPage] ❌ Rejecting match:', matchId);

    const reason = prompt('Enter rejection reason (optional):');

    try {
      await reviewMatch(matchId, 'reject', reason || '');
      console.log('[AdminMatchesPage] Match rejected successfully');

      showSuccess('Match Rejected', 'Match marked as rejected');
      setSelectedMatchId(null);

      // Refresh list
      console.log('[AdminMatchesPage] Refreshing matches after rejection');
      fetchMatches(currentPage, 20, statusFilter, sortBy);
    } catch (err) {
      console.error('[AdminMatchesPage] ❌ Reject error:', err);
      showError('Failed', error || 'Failed to reject match');
    }
  };

  const handleConnectMatch = async (matchId) => {
    console.log('[AdminMatchesPage] 🔗 Connecting parties for match:', matchId);

    const adminNotes = prompt('Add notes about this connection (optional):');

    setConnectingId(matchId);

    try {
      const result = await connectionsAPI.connectParties(matchId, adminNotes || '');
      console.log('[AdminMatchesPage] ✅ Parties connected successfully:', result);

      showSuccess(
        'Connection Created',
        'Parties have been connected and introduction messages sent'
      );

      // Refresh matches list to show updated status
      console.log('[AdminMatchesPage] Refreshing matches after connection');
      fetchMatches(currentPage, 20, statusFilter, sortBy);
      setSelectedMatchId(null);
    } catch (err) {
      console.error('[AdminMatchesPage] ❌ Connect error:', err);
      showError('Connection Failed', err?.response?.data?.message || 'Failed to connect parties');
    } finally {
      setConnectingId(null);
    }
  };

  // ========================================
  // LOADING STATE
  // ========================================

  if (loading && matches.length === 0) {
    console.log('[AdminMatchesPage] ⏳ Loading matches');
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading matches...</p>
        </div>
      </div>
    );
  }

  // ========================================
  // ERROR STATE
  // ========================================

  if (error && matches.length === 0) {
    console.error('[AdminMatchesPage] 🔴 Error state:', error);
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  // ========================================
  // RENDER
  // ========================================

  console.log('[AdminMatchesPage] 🎨 Rendering with matches:', {
    count: matches.length,
    total: pagination.total
  });

  const newMatchesCount = matches.filter(m => m.status === 'pending_review').length;

  return (
    <div className="space-y-6">
      {/* ========================================
          PAGE HEADER
          ======================================== */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Match Management</h1>
        <p className="text-muted-foreground">
          Review and manage all matches between landowners and investors
        </p>
      </div>

      {/* ========================================
          ACTION BUTTONS
          ======================================== */}
      <div className="flex gap-3 flex-wrap">
        <Button
          onClick={handleRunMatching}
          disabled={runningMatching}
          className="gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
        >
          {runningMatching ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Running Matching...
            </>
          ) : (
            <>
              <Zap className="w-4 h-4" />
              Run Matching Algorithm
            </>
          )}
        </Button>
      </div>

      {/* ========================================
          NEW MATCHES ALERT
          ======================================== */}
      {newMatchesCount > 0 && (
        <Alert className="border-blue-500 bg-blue-50">
          <Zap className="h-4 w-4 text-blue-700" />
          <AlertTitle className="text-blue-900">
            {newMatchesCount} New Match{newMatchesCount !== 1 ? 'es' : ''} Pending Review
          </AlertTitle>
          <AlertDescription className="text-blue-800">
            Review and approve these matches to connect the parties
          </AlertDescription>
        </Alert>
      )}

      {/* ========================================
          FILTERS AND SORTING
          ======================================== */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Status Filter */}
          <div>
            <label className="text-sm font-semibold mb-2 block">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending_review">Pending Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="connected">Connected</SelectItem>
                <SelectItem value="all">All Statuses</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sort By */}
          <div>
            <label className="text-sm font-semibold mb-2 block">Sort By</label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="score">Highest Score First</SelectItem>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="oldest">Oldest First</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Results Count */}
          <div className="flex items-end">
            <div className="text-sm">
              <p className="text-muted-foreground">Results</p>
              <p className="text-2xl font-bold">
                {matches.length} / {pagination.total}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ========================================
          MATCHES LIST
          ======================================== */}
      {matches.length === 0 ? (
        <Card>
          <CardContent className="flex items-center justify-center h-32">
            <p className="text-sm text-muted-foreground">
              No matches found. Try running the matching algorithm.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {matches.map(match => (
            <MatchCard
              key={match.id}
              match={match}
              isNew={match.status === 'pending_review'}
              isReviewing={reviewingId === match.id}
              isConnecting={connectingId === match.id}
              onViewDetails={() => handleViewDetails(match)}
              onApprove={() => handleApproveMatch(match.id)}
              onReject={() => handleRejectMatch(match.id)}
              onConnect={() => handleConnectMatch(match.id)}
            />
          ))}
        </div>
      )}

      {/* ========================================
          PAGINATION
          ======================================== */}
      {pagination.pages > 1 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(pagination.pages, 7) }, (_, i) => i + 1).map(
                  page => (
                    <Button
                      key={page}
                      variant={currentPage === page ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Button>
                  )
                )}
              </div>

              <Button
                variant="outline"
                onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))}
                disabled={currentPage === pagination.pages}
              >
                Next
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loadingDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <p className="text-sm text-muted-foreground">Loading match details...</p>
          </div>
        </div>
      )}

      {/* ========================================
          MATCH DETAILS MODAL
          ======================================== */}
      <MatchDetailsModal
        match={viewingMatch}
        isOpen={!!viewingMatch}
        onClose={() => setViewingMatch(null)}
      />
    </div>
  );
}